/**
 * /checkin — mobile-first welcome for Check In with Dr. C.
 * (Also the landing page at connect.christyvonta.com)
 *
 * Approved Version 1.0 copy — do not swap in marketing prose or a
 * biography. The intent is warmth, permission, and a clear invitation
 * to sit down.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { CheckInShell, CheckInLogo, PrimaryButton, GhostButton, CHECKIN_PALETTE } from "./_shell";
import { getToken } from "@/lib/checkinApi";

export default function CheckInWelcome() {
  const alreadySignedIn = !!getToken();
  const [meetOpen, setMeetOpen] = useState(false);
  return (
    <CheckInShell testid="checkin-welcome">
      <div className="pt-4">
        <CheckInLogo />
      </div>

      <div className="mt-12 mb-2">
        <div
          className="text-[10px] font-semibold uppercase tracking-[0.28em]"
          style={{ color: CHECKIN_PALETTE.purple }}
          data-testid="welcome-eyebrow"
        >
          You don&rsquo;t have to carry it alone.
        </div>
        <h1
          className="mt-3 font-serif leading-[1.05] tracking-tight text-4xl sm:text-5xl"
          style={{ color: CHECKIN_PALETTE.ink, fontFamily: "'Cormorant Garamond', Georgia, serif" }}
          data-testid="welcome-headline"
        >
          Check in with Dr.&nbsp;C.
        </h1>

        <p
          className="mt-6 text-[15.5px] leading-relaxed"
          style={{ color: "#2E2439" }}
          data-testid="welcome-primary-copy"
        >
          Some thoughts are too heavy to carry alone. This is a private
          space to pause, think out loud, process what is weighing on
          you, and feel heard without judgment.
        </p>
        <p
          className="mt-3 text-[14px] leading-relaxed"
          style={{ color: "#3E3548" }}
          data-testid="welcome-primary-copy-2"
        >
          Whether you are the strong friend everyone depends on, feeling
          isolated, emotionally exhausted, or simply need someone to
          help you sort through your thoughts — you can check in here.
        </p>
        <p
          className="mt-4 text-[13.5px] leading-relaxed italic"
          style={{ color: "#6C6478" }}
          data-testid="welcome-reassurance"
        >
          Because sometimes the strongest people have no one they can
          be completely honest with.
        </p>
      </div>

      <div className="mt-6">
        <button
          type="button"
          onClick={() => setMeetOpen(v => !v)}
          className="w-full text-left rounded-2xl border p-4 transition-colors"
          style={{
            borderColor: CHECKIN_PALETTE.silverSoft,
            background: meetOpen ? "rgba(92,30,138,0.03)" : "white",
          }}
          data-testid="meet-drc-toggle"
          aria-expanded={meetOpen}
          aria-controls="meet-drc-panel"
        >
          <div className="flex items-center justify-between">
            <div className="text-[11px] uppercase tracking-[0.22em] font-semibold"
                 style={{ color: CHECKIN_PALETTE.purple }}>
              Meet Dr. C
            </div>
            <span aria-hidden="true" style={{ color: CHECKIN_PALETTE.purple }}>
              {meetOpen ? "–" : "+"}
            </span>
          </div>
          {meetOpen && (
            <div id="meet-drc-panel" className="mt-3 space-y-3"
                 data-testid="meet-drc-panel">
              <p className="text-[13.5px] leading-relaxed" style={{ color: "#2E2439" }}>
                I&rsquo;m Dr. Christy Vonta. I created this space because everyone
                needs somewhere they can speak honestly, feel understood,
                and work through what they are carrying.
              </p>
              <p className="text-[13.5px] leading-relaxed" style={{ color: "#3E3548" }}>
                My work brings together psychology, identity development,
                coaching, and whole-person wellness. Check In with Dr. C
                is designed around my approach to listening, asking
                thoughtful questions, offering honest guidance, and
                helping people move forward one conversation at a time.
              </p>
              <a
                href="https://christyvonta.com"
                target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-[13px] font-medium"
                style={{ color: CHECKIN_PALETTE.purple }}
                data-testid="meet-drc-external-link"
                onClick={(e) => e.stopPropagation()}
              >
                Get to know Dr. C&nbsp;→
              </a>
            </div>
          )}
        </button>
      </div>

      <div className="mt-auto pt-8 space-y-3">
        {alreadySignedIn ? (
          <Link to="/checkin/home">
            <PrimaryButton testid="welcome-continue">
              Continue your check-in
            </PrimaryButton>
          </Link>
        ) : (
          <>
            <Link to="/checkin/register" data-testid="welcome-start-link">
              <PrimaryButton testid="welcome-start">
                Begin your check-in
              </PrimaryButton>
            </Link>
            <Link to="/checkin/signin" data-testid="welcome-signin-link">
              <GhostButton testid="welcome-signin">Sign in</GhostButton>
            </Link>
          </>
        )}
        <p
          className="mt-6 text-center text-[11px] leading-relaxed"
          style={{ color: "#7A7288" }}
          data-testid="welcome-privacy-copy"
        >
          Your check-ins are designed to be private and supportive.
          Check In with Dr. C is intended for reflection, education,
          and coaching support. It is not an emergency service and
          does not replace medical, mental-health, or crisis care.
        </p>
      </div>
    </CheckInShell>
  );
}
